<template>
  <div class="market-dropdown-wrapper">

    <div class="selector-btn" @click.stop="open" v-if="!isSmallScreen">
      <span class="selector-label">{{ label }}</span>
      <IllinoisIcons icon="chevron-down" style="color: var(--color-accent, #00e676); height: 16px; width: 16px;"/>
    </div>

    <button class="menu-select-event" @click.stop="open" v-else>
      <IllinoisIcons icon="hamburger" height="16px" width="16px"/>
    </button>

    <div ref="dropdownContainer" class="teleport-dropdown">

      <div v-if="isOpen" class="overlay" @click.stop="close"></div>

      <transition name="fade">
        <div
            v-if="isOpen"
            class="dropdown-panel"
            :style="dropdownStyle"
            v-click-outside="close"
        >
          <div class="panel-header">
            <span class="panel-title">{{ label }}</span>
            <button class="close-btn" @click.stop="close">
              <v-icon dark small>mdi-close</v-icon>
            </button>
          </div>

          <div class="panel-content">
            <div v-for="(group, index) in groupedEventsByTournament" :key="index" class="tournament-group">

              <div class="tournament-header">
                {{ group.name }}
              </div>

              <div
                  v-for="event in group.events"
                  :key="event.id"
                  class="match-row"
                  :class="event.id === id ? 'active' : ''"
                  @click="goToEventDetail(event)"
              >
                <div class="match-time">
                  {{ getMatchCurrentTime(event.livescore.scoreboard.time) }}
                  <span v-if="event.livescore.scoreboard.time && event.livescore.scoreboard.time !== '-1' && event.livescore.scoreboard.time !== '0'" class="blink_me">'</span>
                  <span v-else>{{ getInfoTimeMatch(event.livescore, event.sportId, event.status) }}</span>
                </div>

                <div class="match-details">
                  <div class="team home">{{ event.home }}</div>
                  <div class="scores">
                    <span class="score">{{ genericScore(event.livescore, 'home') }}</span>
                    <span class="score">{{ genericScore(event.livescore, 'away') }}</span>
                  </div>
                  <div class="team away">{{ event.away }}</div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </transition>
    </div>

  </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import LogoCompetitors from "@/components/center_widgets/LogoCompetitors.vue";
import IllinoisIcons from "@/components/american/icons.vue";

export default {
  name: "QuickSelectEventLiveAmerican",
  components: {IllinoisIcons, LogoCompetitors},
  props: {
    items: Array,
    value: Object,
    isMobile: Boolean,
    label: String,
    id: String,
    isSmallScreen: {
      type: Boolean,
      default: false
    }
  },
  emits: ['is-loading'],

  data() {
    return {
      isOpen: false,
      isSmallScreen: false,
      buttonRect: null,
    }
  },

  computed: {
    ...mapGetters({
      'getPreviewLive': 'previewLive/getPreviewLive'
    }),

    groupedEventsByTournament() {
      if (!this.getPreviewLive || !this.getPreviewLive.events) return [];

      let events = this.getPreviewLive.events.flat();
      let filteredEvents = events.filter(e => e.id !== this.id).slice(0, 30);

      const groups = {};
      filteredEvents.forEach(e => {
        const groupName = `${e.categoryName} - ${e.tournamentName}`;
        if (!groups[groupName]) {
          groups[groupName] = {
            name: groupName,
            categoryId: e.categoryId,
            events: []
          };
        }
        groups[groupName].events.push(e);
      });

      return Object.values(groups);
    },

    dropdownStyle() {
      if (!this.buttonRect) return {};

      if (this.isSmallScreen) return {}; // CSS del Mobile gestisce il fullscreen

      const width = 420;

      // 1. Trova il centro esatto del pulsante (che ora è largo quanto il testo)
      const buttonCenter = this.buttonRect.left + (this.buttonRect.width / 2);

      // 2. Allinea il centro del dropdown con il centro del pulsante
      let leftPos = buttonCenter - (width / 2);

      // 3. Impedisce al dropdown di uscire fuori dai bordi dello schermo
      if (leftPos < 10) {
        leftPos = 10;
      } else if (leftPos + width > window.innerWidth) {
        leftPos = window.innerWidth - width - 10;
      }

      return {
        top: `${this.buttonRect.bottom + 8}px`,
        left: `${leftPos}px`,
      };
    },
  },

  mounted() {
    if (this.$refs.dropdownContainer) {
      document.body.appendChild(this.$refs.dropdownContainer);
    }
    window.addEventListener('resize', this.onResize);
    this.onResize();
  },

  beforeDestroy() {
    if (this.$refs.dropdownContainer && this.$refs.dropdownContainer.parentNode) {
      this.$refs.dropdownContainer.parentNode.removeChild(this.$refs.dropdownContainer);
    }
    window.removeEventListener('resize', this.onResize);
  },

  methods: {
    ...mapActions({
      'fetchEventLiveDetail': 'detailLive/fetchEventLiveDetailV5'
    }),

    getMatchCurrentTime(time) {
      if (time === undefined || time === null || time === '') return '';
      const timeNum = parseInt(time, 10);
      if (isNaN(timeNum) || timeNum < 0) return '';
      var minutes = Math.floor(timeNum / 60);
      var seconds = timeNum - minutes * 60;
      return (seconds < 10) ? minutes + ':0' + seconds : minutes + ':' + seconds;
    },

    getInfoTimeMatch(livescore, sport, status) {
      if (status === '9' || (livescore && livescore.scoreboard && livescore.scoreboard.currentPeriod === '-1')) {
        return this.$t('periods.about_to_start');
      }
      if (livescore && livescore.scoreboard && livescore.scoreboard.currentPeriod) {
        return this.$t('periods.' + sport + '.' + livescore.scoreboard.currentPeriod);
      }
      return '';
    },

    onResize() {
      const w = window.innerWidth;
      this.isSmallScreen = w <= 600;
      if (this.isOpen) {
        this.updateRect();
      }
    },

    goToEventDetail(event) {
      this.getDetailOfEventLive(event.id, event.type, event.sportId, (event.tournamentId) ? event.tournamentId : '', event.status);
    },

    async getDetailOfEventLive(eventSource, type, sport, tournament, status) {
      this.close();
      this.$emit('is-loading', true);
      this.$root.$emit('isloading_quick_select_event_live', true);
      await this.fetchEventLiveDetail({ 'source': eventSource, 'group': 0, 'sport': sport, 'tournament': tournament, 'status': status });
      this.$emit('is-loading', false);
      this.$root.$emit('isloading_quick_select_event_live', false);
    },

    genericScore(livescore, position) {
      if (!livescore || !livescore.scoreboard || !livescore.scoreboard.results) {
        return '-';
      }
      const results = livescore.scoreboard.results;
      if (position === 'home')
        return results.find(r => r.position === '1')?.value ?? '0';
      else
        return results.find(r => r.position === '2')?.value ?? '0';
    },

    updateRect() {
      const btn = this.$el.querySelector('.selector-btn');
      this.buttonRect = btn ? btn.getBoundingClientRect() : null;
    },

    open() {
      this.updateRect();
      this.isOpen = true;
      document.body.classList.add("no-scroll");
      const stickyNav = this.$el.closest('.sticky-nav');
      if (stickyNav) { stickyNav.style.zIndex = '10000'; stickyNav.style.position = 'sticky'; }
    },

    close() {
      this.isOpen = false;
      document.body.classList.remove("no-scroll");
      const stickyNav = this.$el.closest('.sticky-nav');
      if (stickyNav) {
        setTimeout(() => { stickyNav.style.zIndex = ''; stickyNav.style.position = ''; }, 250);
      }
    }
  }
}
</script>

<style scoped>
.market-dropdown-wrapper {
  position: relative;
}

/* PULSANTE CORRETTO: Adesso occupa solo lo spazio della scritta e si centra */
.selector-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  padding: 10px 16px;
  min-height: 44px;
  width: fit-content; /* Rimpicciolisce il div in base al testo */
  margin: 0 auto; /* Lo centra all'interno del wrapper */
  touch-action: manipulation;
  transition: opacity 0.2s ease;
}

.selector-btn:active {
  opacity: 0.6;
}

.selector-label {
  font-size: var(--font-size-7xl);
  font-weight: 800;
  color: var(--text-platform);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.selector-btn:hover .selector-label {
  text-decoration: underline;
}

/* OVERLAY TELEPORTATO */
.teleport-dropdown {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2147483647;
}

.overlay {
  position: fixed;
  inset: 0;
  z-index: 2147483645;
  cursor: pointer;
}

/* PANNELLO PRINCIPALE */
.dropdown-panel {
  position: fixed;
  width: 420px;
  max-height: 65vh;
  display: flex;
  flex-direction: column;
  background: var(--bg-body);
  border-radius: 8px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.8);
  border: 1px solid var(--hsla-1);
  z-index: 2147483646;
  overflow: hidden;
  font-family: Arial, sans-serif;
}

/* HEADER DEL PANNELLO */
.panel-header {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 14px 16px;
  background: var(--bg-header);
  border-bottom: 1px solid var(--hsla-1);
  position: relative;
  flex-shrink: 0;
}

.panel-title {
  color: var(--text-platform);
  font-weight: 700;
  font-size: var(--font-size-5xl);
  overflow: hidden;
  max-width: 78%;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.close-btn {
  position: absolute;
  right: 15px;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-platform);
  transition: background 0.2s;
}
.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* CORPO SCROLLABILE */
.panel-content {
  overflow-y: auto;
  flex: 1;
}

.panel-content::-webkit-scrollbar {
  width: 6px;
}
.panel-content::-webkit-scrollbar-thumb {
  background-color: #444;
  border-radius: 3px;
}

/* HEADER DEL TORNEO */
.tournament-header {
  background: var(--bg-card);
  color: var(--text-platform);
  font-size: var(--font-size-2xl);
  font-weight: 700;
  text-align: center;
  padding: 8px;
  border-bottom: 1px solid var(--hsla-1);
}

/* SINGOLA ROW DELLA PARTITA */
.match-row {
  padding: 14px 16px;
  border-bottom: 1px solid var(--hsla-1);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background-color: transparent;
  transition: background-color 0.15s ease;
}



.match-row.active {
  border-left: 3px solid var(--color-accent, #00e676);
}

/* TEMPO */
.match-time {
  text-align: center;
  font-size: var(--font-size-xl);
  color: #aaaaaa;
}

/* SQUADRE E RISULTATO */
.match-details {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.team {
  flex: 1;
  font-size: var(--font-size-3xl);
  font-weight: 700;
  color: var(--text-platform);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.team.home { text-align: right; }
.team.away { text-align: left; }

.scores {
  flex: 0 0 auto;
  display: flex;
  justify-content: center;
  gap: 16px;
  margin: 0 20px;
}

.scores .score {
  color: var(--tracker-score-color);
  font-size: var(--font-size-8xl);
  font-weight: 900;
}

/* ANIMAZIONI DI ENTRATA E USCITA */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* MOBILE */
@media (max-width: 600px) {
  .dropdown-panel {
    width: 100vw;
    height: 100vh;
    max-height: 100vh;
    top: 0 !important;
    left: 0 !important;
    border-radius: 0;
    border: none;
  }

  .fade-enter, .fade-leave-to {
    transform: translateY(20px);
  }
}

.menu-select-event{
  height: 34px;
  min-width: 30px;
  border-radius: 99px;
  background-color: rgba(0, 0, 0, 0.2);
  color: var(--color-accent);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0 10px;
  font-size: var(--font-size-xl);
  font-weight: 700;
  line-height: 25px;
  position: relative;
  padding-top: 6px;
}
</style>